<template>
  <div class="app-shell">

    <!-- App-wide confirm dialog (useConfirm); toasts are mounted in App.vue -->
    <UiConfirmDialog />

    <!-- Sidebar (Laravel starter kit style): see AppSidebar.vue -->
    <AppSidebar
      :links="visibleLinks"
      :base-path="`/${userType}`"
      :user="sidebarUser"
      :roles="isValidated == 1 ? roleOptions : []"
      :current-role-id="currentModuleId"
      :switching-role="isLoading"
      :badges="navBadges"
      @switch-role="switchRole"
      @logout="confirmLogout"
    />

    <!-- Content: an inset rounded panel with its own header, like the
         Laravel starter kit. The page itself follows the header directly. -->
    <v-main class="app-main">
      <div
        class="inset"
        :class="{
          'inset--collapsed': sidebar.collapsed.value,
          'inset--mobile': sidebar.isMobile.value,
        }"
      >
        <header class="inset-header">
          <button
            type="button"
            class="sidebar-trigger"
            aria-label="Toggle sidebar"
            title="Toggle sidebar (Ctrl/⌘ B)"
            @click="sidebar.toggle()"
          >
            <v-icon size="16">mdi-dock-left</v-icon>
          </button>
          <nav class="breadcrumbs" aria-label="Breadcrumb">
            <span class="breadcrumbs__current">{{ $route.meta.title }}</span>
          </nav>

          <v-spacer />

          <v-select
            v-if="!$route.meta.hideYearPicker"
            :model-value="$store.state.filterSelected"
            :items="schoolYears.years"
            item-title="school_year"
            item-value="id"
            density="compact"
            hide-details
            prepend-inner-icon="mdi-calendar-blank-outline"
            aria-label="School year"
            class="inset-header__year"
            @update:modelValue="changeFilter"
          />

          <NotificationBell
            :user-id="$store.state.user?.id"
            :role-code="$store.getters.activeRoleCode"
            :base-path="`/${userType}`"
          />
        </header>

        <div class="page">
          <router-view @reloadProfile="loadImg" />
        </div>
      </div>
    </v-main>

    <!--Logout Dialog-->

    <!--Change Assign Module Dialog-->
    <fade-away-message-component
      displayType="variation2"
      v-model="fadeAwayMessage.show"
      :message="fadeAwayMessage.message"
      :header="fadeAwayMessage.header"
      :top="fadeAwayMessage.top"
      :type="fadeAwayMessage.type"
    ></fade-away-message-component>
  </div>
</template>

<script>
import AppSidebar from './AppSidebar.vue';
import NotificationBell from '@/components/notifications/NotificationBell.vue';
import UiConfirmDialog from '@/components/ui/UiConfirmDialog.vue';

// A school year graded in 3 terms (syType 1) doesn't use Senior High
// strands, so the page is left out of the menu. Saved menus may still hold
// the old /add-strands address.
const STRANDS_ROUTES = ['/strands', '/add-strands'];
const usesTerms = (syType) => Number(syType) === 1;

import { useToast } from '@/composables/useToast';
import { reactive } from 'vue';
import { useSidebar } from '@/composables/useSidebar';
import { useSchoolYears } from '@/composables/useSchoolYears';
import { RoleCode } from '@/constants/accounts';
import { useProfilePhoto } from '@/composables/useProfilePhoto';
import { useConfirm } from '@/composables/useConfirm';
import { usePendingAccounts } from '@/composables/usePendingAccounts';
import { areaFor } from '@/api/session';

const ACCOUNTS_ROUTE = '/accounts';
// How often moving between pages may re-check the waiting registrations.
const PENDING_RECHECK_MS = 30 * 1000;
export default {
  components: { AppSidebar, NotificationBell, UiConfirmDialog },
  setup() {
    return {
      sidebar: useSidebar(),
      confirm: useConfirm().confirm,
      toast: useToast(),
      // reactive() so the template can read schoolYears.years directly
      schoolYears: reactive(useSchoolYears()),
      profilePhoto: reactive(useProfilePhoto()),
      pendingAccounts: reactive(usePendingAccounts()),
    };
  },
  name: 'NavbarPage',
  data() {
    return {
      drawer: true,
      selectedModuleId: null,
      selected: [],
      isLoading: false,
      selectedDate: new Date().toISOString().substr(0, 10),
      accountMenu: false,
      menu: [
        {
          title: 'Dashboard',
          icon: 'mdi-view-dashboard',
          active: true,
          items: ['Dashboard 1', 'Dashboard 2'],
        },
        {
          title: 'Apps',
          icon: 'mdi-apps',
          active: false,
          items: ['Chat', 'Calendar'],
        },
        {
          title: 'UI Elements',
          icon: 'mdi-palette',
          active: false,
          items: ['Buttons', 'Avatars'],
        },
      ],
      fadeAwayMessage: {
        show: false,
        type: 'success',
        header: 'Successfully Added!',
        message: '',
        top: 10,
      },
      mini: false,
      mobile: false,
      userRoleList: [],
      links: [],
      userID: null,
      userType: null,
      openGroups: [],
      loading: false,
      options: [],
      activeGroup: null,
      schooYearList: [],
      selectedFilter: null,
      filterYears: [],
      userModule: null,
      isValidated: null,
      assigneAccessModulesList: [],
    };
  },

  computed: {
    // Whether this role's menu has the Accounts page, top level or in a group.
    hasAccountsPage() {
      return this.links.some(
        (link) => link.route === ACCOUNTS_ROUTE || (link.subLink || []).some((sub) => sub.route === ACCOUNTS_ROUTE),
      );
    },
    // Number pills in the sidebar, by menu route.
    navBadges() {
      return this.hasAccountsPage ? { [ACCOUNTS_ROUTE]: this.pendingAccounts.count } : {};
    },
    currentModuleId() {
      return this.$store.getters.activeRole?.id ?? Number(this.userModule);
    },
    // Granted roles for the sidebar's role switcher. Subject Teacher and
    // Class Adviser are one entry: Class Adviser covers the subject classes
    // too, so an account holding both only sees Class Adviser (the server
    // maps a switch to Subject Teacher the same way).
    roleOptions() {
      const roles = this.assigneAccessModulesList.map((item) => ({
        ...item,
        id: Number(Array.isArray(item.id) ? item.id[0] : item.id),
      }));
      const hasAdviser = roles.some((r) => r.code === RoleCode.Adviser);
      return hasAdviser ? roles.filter((r) => r.code !== RoleCode.Teacher) : roles;
    },
    selectedRole() {
      return this.roleOptions.find((r) => r.id === this.selectedModuleId) || null;
    },
    // The role's menu minus pages the selected school year doesn't use.
    visibleLinks() {
      if (!usesTerms(this.$store.getters.getSyType)) return this.links;
      const shown = (link) => !STRANDS_ROUTES.includes(link.route);
      return this.links
        .filter(shown)
        .map((link) => (link.subLink ? { ...link, subLink: link.subLink.filter(shown) } : link))
        .filter((link) => !link.subLink || link.subLink.length);
    },
    // Signed-in user as shown at the bottom of the sidebar.
    sidebarUser() {
      const u = this.$store.state.user || {};
      return {
        name: [u.fname, u.lname].filter(Boolean).join(' '),
        email: u.user?.email || '',
        avatar: this.profilePhoto.url || null,
      };
    },
  },
  mounted() {
    this.userId = this.$store.state.user.id;
    this.userModule = localStorage.getItem('AssignedModID');
    this.subModules = this.$store.state.user.user.subModules;
    this.$store.commit('SET_ASSIGNED_MODULE', this.userModule);
    localStorage.setItem('AssignedModID', this.userModule);
    this.isValidated = this.$store.state.user.user.isValidated;
    this.loadYearForFilter();
    this.getAccessControlAssignedModules();
    this.loadImg();

    if (this.$vuetify.display.xs) {
      this.drawer = false;
      this.mini = false;
    }
  },
  methods: {
    // Shared with the School Year page (useSchoolYears), so edits there
    // refresh this picker without reloading the app.
    async confirmLogout() {
      const ok = await this.confirm({
        title: 'Log out?',
        description: "You'll need to sign in again to continue.",
        confirmText: 'Log out',
      });
      if (ok) this.logout();
    },
    loadYearForFilter() {
      this.schoolYears.load().catch(() => {});
    },
    changeFilter(newValue) {
      this.schoolYears.select(newValue);
    },
    getMyRole(id) {
      const match = this.userRoleList.find(
        (r) => parseInt(r.id) === parseInt(id),
      );
      return match ? match.description : '';
    },
    openMobileNav() {
      this.drawer = true;
    },
    // The user's photo for the sidebar menu (also re-run on @reloadProfile).
    loadImg() {
      this.profilePhoto.refresh();
    },
    toProfile() {
      this.$router.push(`/${areaFor(this.$store.state.user)}/settings`);
    },
    getCurrentDate() {
      const cur_date = new Date();
      const month = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
      ];
      const day = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
      ];
      return `${day[cur_date.getDay()]}, ${
        month[cur_date.getMonth()]
      } ${cur_date.getDate()}; ${this.formatAMPM(cur_date)}`;
    },
    formatAMPM(date) {
      let hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'pm' : 'am';
      hours = hours % 12 || 12;
      return `${hours}:${minutes} ${ampm}`;
    },
    closeNav() {
      this.drawer = false;
      this.$emit('closeNav');
    },
    recheckPendingAccounts(force = false) {
      if (!this.hasAccountsPage) return;
      const now = Date.now();
      if (!force && now - (this.pendingCheckedAt || 0) < PENDING_RECHECK_MS) return;
      this.pendingCheckedAt = now;
      this.pendingAccounts.refresh();
    },
    loadMenu() {
      this.openGroups = this.links.map((link) => !!link.subLink && false);
      this.axiosCall('/assigned-modules/getMyAssignedModules/my', 'GET').then(
        (resp) => {
          this.selectedModuleId = resp.data.id;
          this.links = JSON.parse(resp.data.assign_mods);
          // Pages decide what to show from the role's code and level.
          this.$store.commit('setActiveRole', resp.data);
          // Some legacy dialogs still read the id from localStorage.
          localStorage.setItem('AssignedModID', resp.data.id);
          // Same area the router keeps the user in, so menu links resolve.
          this.userType = areaFor(this.$store.state.user);
        },
      );
    },

    getAccessControlAssignedModules() {
      this.axiosCall('/assigned-modules/getSpecificModules', 'GET').then(
        (res) => {
          let subModules = this.$store.state.user.user.subModules;

          if (typeof subModules === 'string') {
            try {
              subModules = JSON.parse(subModules);
            } catch (error) {
              subModules = [];
            }
          }

          subModules = Array.isArray(subModules) ? subModules : [];

          this.assigneAccessModulesList = res.data.filter((item) =>
            subModules.includes(Number(item.id)),
          );
        },
      );
    },
    switchRole(id) {
      this.selectedModuleId = id;
      this.confirmChangeRole();
    },
    async confirmChangeRole() {
      this.isLoading = true;

      const userID = this.$store.state.user.user.id;

      const role = this.selectedRole;
      const data = { assignedModuleID: role.id };

      try {
        const res = await this.axiosCall(
          '/auth/changeAssignedModule/Role/' + userID,
          'POST',
          data,
        );

        if (res?.data?.status == 200) {
          // Update Vuex
          this.$store.commit('SET_ASSIGNED_MODULE', role.id);

          // Update localStorage
          localStorage.setItem('AssignedModID', role.id);

          this.isLoading = false;

          // Wait for router navigation
          await this.$router.push('/');

          // Reload after navigation has completed
          location.reload();
        } else {
          // Includes 403 when the role isn't one the admin granted.
          this.isLoading = false;

          this.toast.error(res?.data?.msg || res?.data?.message || 'Could not switch role.');
        }
      } catch (error) {
        this.isLoading = false;
        this.toast.error('Could not switch role.');
      }
    },
    // Switching to a 3-term year (or opening an old link) while on Strands
    // would leave the user on a page the menu no longer offers.
    leaveHiddenPage() {
      if (!usesTerms(this.$store.getters.getSyType)) return;
      const page = this.$route.path.replace(/^\/[^/]+/, '').toLowerCase();
      if (STRANDS_ROUTES.includes(page)) this.$router.replace(`/${areaFor(this.$store.state.user)}/dashboard`);
    },
  },
  watch: {
    // Count once the menu shows the Accounts page, then again as the admin
    // moves around, so a new registration shows without a reload.
    hasAccountsPage(has) {
      if (has) this.recheckPendingAccounts(true);
    },
    '$route.path'() {
      this.recheckPendingAccounts();
    },
  },
  created() {
    this.loadMenu();
    this.$watch('$route.path', this.leaveHiddenPage);
    this.$watch(() => this.$store.getters.getSyType, this.leaveHiddenPage);
  },
};
</script>

<style scoped>
/* ============ Shell (Laravel starter kit style) ============ */
/* The sidebar (AppSidebar.vue) sits on the sidebar canvas; the content is an
   inset white panel: m-2 (ml-0 next to the expanded sidebar), rounded-xl,
   shadow-sm, no border. Full-bleed below 768px. */
.app-main {
  background-color: var(--sidebar);
  min-height: 100vh;
  /* Keeps the panel's top margin inside this box. Without it the margin
     collapsed through v-main, and the strip above the panel showed the page
     background instead of the sidebar colour. */
  display: flow-root;
}

.inset {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 16px);
  margin: 8px 8px 8px 0;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: 14px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  transition: margin 0.2s linear;
}
.inset--collapsed {
  margin-left: 8px;
}
.inset--mobile {
  margin: 0;
  min-height: 100vh;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

/* AppSidebarHeader: trigger + breadcrumbs; shorter when collapsed */
.inset-header {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 64px;
  padding: 0 16px;
  background-color: color-mix(in srgb, var(--background) 92%, transparent);
  backdrop-filter: blur(6px);
  border-bottom: 1px solid color-mix(in srgb, var(--border) 50%, transparent);
  /* Just inside the panel's 1px border */
  border-radius: 13px 13px 0 0;
  transition: height 0.2s linear;
}
.inset--collapsed .inset-header {
  height: 48px;
}
.inset--mobile .inset-header {
  border-radius: 0;
  padding: 0 24px;
}

.sidebar-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: -4px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--foreground);
  cursor: pointer;
}
.sidebar-trigger:hover {
  background-color: var(--accent);
}
.sidebar-trigger:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(161, 161, 161, 0.5);
}

.breadcrumbs {
  display: flex;
  align-items: center;
  min-width: 0;
  font-size: 15px;
  color: var(--muted-foreground);
  white-space: nowrap;
}
.breadcrumbs__current {
  color: var(--foreground);
  overflow: hidden;
  text-overflow: ellipsis;
}

.inset-header__year {
  flex: 0 0 190px;
  max-width: 190px;
}
@media (max-width: 600px) {
  .inset-header__year {
    flex-basis: 140px;
    max-width: 140px;
  }
}

/* Page content: full width, p-4, like the starter kit pages */
.page {
  display: flex;
  flex-direction: column;
  gap: 20px;
  flex: 1;
  width: 100%;
  padding: 20px;
  overflow-x: auto;
}

/* Role selection card */



/* Focus visibility for accessibility */

@media (prefers-reduced-motion: reduce) {
  .inset,
  .inset-header {
    transition: none !important;
  }
}
</style>
